import { useState } from 'react'

import type { Appointment } from '../../api/timetable'
import { Button } from '../../ui/Control'
import { type Repetition, RepeatFields, defaultUntil, weekdayOfDay } from './RepeatFields'

/**
 * Whether this appointment repeats — a switch on the appointment itself, not a screen of its own.
 *
 * A repetition is never what somebody sets out to make. They write down the Tuesday lecture, look at
 * it, and *then* say "this happens every week until February". Everything a rule needs is already
 * standing on the appointment — the day, both times, the room, the subject — so the only things left
 * to ask are how often and until when.
 *
 * **The switch is the action.** Turning it on makes the run, there and then, out of the appointment's
 * own weekday and a term's horizon; the fields below change the rule afterwards, each when it is
 * left, exactly like every other field in MyLib. A button saying "now really do it" would be the
 * confirmation dialog this product does not have.
 */
export interface RepeatsProps {
  readonly appointment: Appointment
  readonly onRepeat: (repetition: Repetition) => void
  readonly onStop: () => void
}

export function Repeats({ appointment, onRepeat, onStop }: RepeatsProps) {
  const repeating = appointment.seriesId !== null
  const day = dayOf(appointment)
  const [repetition, setRepetition] = useState<Repetition>({
    weekdays: [weekdayOfDay(day)],
    everyWeeks: 1,
    until: defaultUntil(day),
  })

  return (
    <section className="appointment-detail__part">
      <div className="repeats__head">
        <button
          type="button"
          className="chip"
          aria-pressed={repeating}
          onClick={() => {
            if (repeating) {
              onStop()
            } else {
              onRepeat(repetition)
            }
          }}
        >
          Repeats
        </button>
        {repeating && (
          <Button tone="bare" onClick={onStop}>
            Stop repeating
          </Button>
        )}
      </div>

      {/* The settings belong to a repetition that exists. Off, there is nothing to set. */}
      {repeating && (
        <RepeatFields
          value={repetition}
          onChange={(wanted) => {
            setRepetition(wanted)
            onRepeat(wanted)
          }}
        />
      )}
    </section>
  )
}

/** The day this appointment falls on, as `YYYY-MM-DD`. */
function dayOf(appointment: Appointment): string {
  const at = new Date(appointment.startsAt)
  const month = String(at.getMonth() + 1).padStart(2, '0')
  const day = String(at.getDate()).padStart(2, '0')
  return `${String(at.getFullYear())}-${month}-${day}`
}
